/**
 * AvatarCastChatPage — fully chat-driven intake for the HeyGen avatar pipeline.
 *
 * Left panel (5/12): conversational step flow
 *   Step 1 → Script
 *   Step 2 → Avatar look (6 preset images)
 *   Step 3 → Voice selection (from /avatar-cast/voices)
 *   Step 4 → Language + aspect ratio
 *   Step 5 → Confirm & generate
 *
 * Right panel (7/12): blueprint status + final video
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  ChevronRight,
  Clapperboard,
  ExternalLink,
  Film,
  Loader2,
  Mic,
  Play,
  Video,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  FlowChatLayout,
  AssistantBubble,
  UserBubble,
  ChatInputArea,
  ChatSection,
  RightPanelHeading,
} from '@/components/FlowChatLayout';
import api from '@/lib/api';
import { withAccessToken } from '@/lib/avatarCastAuthUrl';
import { formatApiError } from '@/lib/formatApiError';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/* ── constants ──────────────────────────────────────────────────────────── */

const LOOK_TEMPLATES = [
  { id: 'look_1', name: 'My Digital Twin', badge: 'Original', icon: '👤', image: null },
  { id: 'look_2', name: 'Blue Hoodie Creator', badge: 'Casual', icon: '🧥', image: '/api/avatar-cast/static-looks/blue_hoodie.png' },
  { id: 'look_3', name: 'Broadcast Studio', badge: 'Studio Pro', icon: '🎙️', image: '/api/avatar-cast/static-looks/studio_pro.png' },
  { id: 'look_4', name: 'Podcast Presenter', badge: 'Podcast', icon: '🎤', image: '/api/avatar-cast/static-looks/silver_mic.png' },
  { id: 'look_5', name: 'Educator Style', badge: 'Educator', icon: '👓', image: '/api/avatar-cast/static-looks/green_shirt.png' },
  { id: 'look_6', name: 'Executive Look', badge: 'Executive', icon: '👔', image: '/api/avatar-cast/static-looks/grey_sweater.png' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
];

const ASPECT_RATIOS = [
  { value: '16:9', label: '16:9 Landscape' },
  { value: '9:16', label: '9:16 Vertical' },
  { value: '1:1', label: '1:1 Square' },
];

const POLL_INTERVAL = 3000;

const STEP = {
  SCRIPT: 'script',
  LOOK: 'look',
  VOICE: 'voice',
  SETTINGS: 'settings',
  CONFIRM: 'confirm',
  GENERATING: 'generating',
  DONE: 'done',
};

/* ── component ──────────────────────────────────────────────────────────── */

export function AvatarCastChatPage() {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  /* ── form state ── */
  const [chatStep, setChatStep] = useState(STEP.SCRIPT);
  const [script, setScript] = useState('');
  const [selectedLook, setSelectedLook] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [language, setLanguage] = useState('en');
  const [aspectRatio, setAspectRatio] = useState('16:9');

  /* ── generation state ── */
  const [generating, setGenerating] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [genStatus, setGenStatus] = useState('');
  const [genProgress, setGenProgress] = useState(0);
  const [genError, setGenError] = useState('');
  const pollRef = useRef(null);

  /* ── scroll to bottom on step change ── */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatStep]);

  /* ── load voices when we reach that step ── */
  useEffect(() => {
    if (chatStep !== STEP.VOICE || voicesLoading || voices.length) return;
    setVoicesLoading(true);
    api.get('/avatar-cast/voices')
      .then(({ data }) => { setVoices(data?.voices || data || []); })
      .catch(() => toast.error('Could not load voices — continue anyway'))
      .finally(() => setVoicesLoading(false));
  }, [chatStep, voices.length, voicesLoading]);

  /* ── polling ── */
  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPoll = useCallback((projId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/avatar-cast/project/${projId}/status`);
        const status = data?.status || '';
        setGenStatus(status);
        setGenProgress(Number(data?.progress || 0));
        if (status === 'completed' || status === 'done') {
          setVideoUrl(data?.video_url || data?.final_video_url || '');
          setChatStep(STEP.DONE);
          stopPoll();
        }
        if (status === 'failed') {
          setGenError(data?.error_message || 'Generation failed');
          setChatStep(STEP.CONFIRM);
          stopPoll();
        }
      } catch { /* ignore poll errors */ }
    }, POLL_INTERVAL);
  }, [stopPoll]);

  useEffect(() => () => stopPoll(), [stopPoll]);

  /* ── step handlers ── */
  const advanceToLook = () => {
    if (!script.trim()) { toast.error('Enter your script first'); return; }
    setChatStep(STEP.LOOK);
  };

  const advanceToVoice = (look) => {
    setSelectedLook(look);
    setChatStep(STEP.VOICE);
  };

  const advanceToSettings = (voice) => {
    setSelectedVoice(voice);
    setChatStep(STEP.SETTINGS);
  };

  const advanceToConfirm = () => setChatStep(STEP.CONFIRM);

  const startGeneration = async () => {
    setGenerating(true);
    setGenError('');
    try {
      const scenes = script.trim().split(/\n{2,}/).filter(Boolean).map((text, idx) => ({
        scene_id: `s${idx + 1}`,
        scene_order: idx + 1,
        script: text.trim(),
        asset_type: 'heygen_avatar',
        look_id: selectedLook?.id || 'look_1',
        voice_id: selectedVoice?.voice_id || selectedVoice?.id || null,
        duration: Math.max(3, Math.ceil(text.trim().split(/\s+/).length / 2.5)),
      }));

      const { data } = await api.post('/avatar-cast/create-blueprint', {
        title: script.trim().split('\n')[0].slice(0, 60) || 'Avatar reel',
        language,
        aspect_ratio: aspectRatio,
        scenes,
        look_id: selectedLook?.id || 'look_1',
        voice_id: selectedVoice?.voice_id || selectedVoice?.id || null,
      });

      const projId = data?.project_id || data?.id;
      if (!projId) throw new Error('No project ID returned');
      setProjectId(projId);
      setBlueprint(data);
      setChatStep(STEP.GENERATING);
      startPoll(projId);
      toast.success('Generation started — watch the progress on the right');
    } catch (err) {
      setGenError(formatApiError(err, 'Could not start generation'));
      toast.error(formatApiError(err, 'Could not start generation'));
    } finally {
      setGenerating(false);
    }
  };

  /* ── Left panel: chat thread ── */
  const leftContent = (
    <>
      {/* Intro */}
      <AssistantBubble first>
        <div className="flex items-center gap-2 mb-1">
          <Video className="w-4 h-4 text-violet-300" />
          <span className="font-semibold text-violet-200 text-[12px] uppercase tracking-wider">AvatarCast</span>
        </div>
        <p>I'll help you create a talking-avatar video. Let's go step by step — script, look, voice, then generate.</p>
      </AssistantBubble>

      {/* STEP: Script */}
      <AssistantBubble>
        <p className="font-medium">What should your avatar say?</p>
        <p className="text-[12px] text-[#9CA3AF] mt-0.5">Paste your full script below. Separate scenes with a blank line.</p>
      </AssistantBubble>
      <ChatInputArea>
        <div className="space-y-2">
          <Textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Welcome to our product demo…

In this video we'll show you how to…"
            rows={6}
            className="bg-[#0d1017] border-white/10 text-sm resize-none"
          />
          {[STEP.SCRIPT].includes(chatStep) ? (
            <Button size="sm" onClick={advanceToLook} disabled={!script.trim()} className="bg-violet-500 hover:bg-violet-400 text-white text-xs">
              Next: choose look →
            </Button>
          ) : null}
        </div>
      </ChatInputArea>

      {/* STEP: Look */}
      <AnimatePresence>
        {[STEP.LOOK, STEP.VOICE, STEP.SETTINGS, STEP.CONFIRM, STEP.GENERATING, STEP.DONE].includes(chatStep) && (
          <motion.div key="look-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <UserBubble>{script.trim().slice(0, 60)}{script.trim().length > 60 ? '…' : ''} ✓</UserBubble>
            <AssistantBubble>Choose your avatar look.</AssistantBubble>
            <ChatInputArea>
              <div className="grid grid-cols-3 gap-2">
                {LOOK_TEMPLATES.map((look) => {
                  const active = selectedLook?.id === look.id;
                  return (
                    <button
                      key={look.id}
                      type="button"
                      onClick={() => chatStep === STEP.LOOK && advanceToVoice(look)}
                      disabled={chatStep !== STEP.LOOK}
                      className={`relative rounded-xl overflow-hidden border transition-all cursor-pointer text-left group ${
                        active ? 'border-violet-400 ring-1 ring-violet-400/50' : 'border-white/10 hover:border-white/30'
                      } ${chatStep !== STEP.LOOK ? 'opacity-60 cursor-default' : ''}`}
                      style={{ aspectRatio: '3/4' }}
                    >
                      {look.image ? (
                        <img src={withAccessToken(look.image)} alt={look.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-violet-500/10 text-3xl">
                          {look.icon}
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5">
                        <p className="text-[10px] font-semibold text-white leading-tight">{look.badge}</p>
                      </div>
                      {active ? (
                        <div className="absolute top-1.5 right-1.5">
                          <CheckCircle2 className="w-4 h-4 text-violet-400" />
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </ChatInputArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STEP: Voice */}
      <AnimatePresence>
        {[STEP.VOICE, STEP.SETTINGS, STEP.CONFIRM, STEP.GENERATING, STEP.DONE].includes(chatStep) && (
          <motion.div key="voice-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <UserBubble>{selectedLook?.name || 'Look chosen'} ✓</UserBubble>
            <AssistantBubble>
              <p className="font-medium">Pick a voice for your avatar.</p>
            </AssistantBubble>
            <ChatInputArea>
              {voicesLoading ? (
                <div className="flex items-center gap-2 text-[12px] text-[#9CA3AF]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading voices…
                </div>
              ) : voices.length ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {voices.slice(0, 20).map((v) => {
                    const vid = v.voice_id || v.id;
                    const active = selectedVoice && (selectedVoice.voice_id || selectedVoice.id) === vid;
                    const disabled = chatStep !== STEP.VOICE;
                    return (
                      <button
                        key={vid}
                        type="button"
                        onClick={() => !disabled && advanceToSettings(v)}
                        disabled={disabled}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-all cursor-pointer text-[12px] ${
                          active ? 'border-violet-400 bg-violet-500/10 text-white' : 'border-white/[0.07] bg-white/[0.02] text-[#E5E7EB] hover:border-white/20'
                        } ${disabled ? 'opacity-60 cursor-default' : ''}`}
                      >
                        <Mic className="w-3.5 h-3.5 text-[#9CA3AF] shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{v.display_name || v.name || vid}</p>
                          {v.language ? <p className="text-[10px] text-[#6B7280]">{v.language}</p> : null}
                        </div>
                        {active ? <CheckCircle2 className="w-4 h-4 text-violet-400 ml-auto shrink-0" /> : null}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[12px] text-[#9CA3AF]">No voices loaded — continue without selecting.</p>
                  {chatStep === STEP.VOICE ? (
                    <Button size="sm" onClick={() => advanceToSettings(null)} className="bg-violet-500 hover:bg-violet-400 text-white text-xs">Continue anyway →</Button>
                  ) : null}
                </div>
              )}
            </ChatInputArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STEP: Settings */}
      <AnimatePresence>
        {[STEP.SETTINGS, STEP.CONFIRM, STEP.GENERATING, STEP.DONE].includes(chatStep) && (
          <motion.div key="settings-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <UserBubble>{selectedVoice ? (selectedVoice.display_name || selectedVoice.name || 'Voice chosen') : 'No voice'} ✓</UserBubble>
            <AssistantBubble>Almost there — choose language and frame size.</AssistantBubble>
            <ChatInputArea>
              <div className="flex gap-2 flex-wrap">
                <Select value={language} onValueChange={setLanguage} disabled={chatStep !== STEP.SETTINGS}>
                  <SelectTrigger className="bg-[#0d1017] border-white/10 text-xs h-9 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={aspectRatio} onValueChange={setAspectRatio} disabled={chatStep !== STEP.SETTINGS}>
                  <SelectTrigger className="bg-[#0d1017] border-white/10 text-xs h-9 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    {ASPECT_RATIOS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {chatStep === STEP.SETTINGS ? (
                  <Button size="sm" onClick={advanceToConfirm} className="bg-violet-500 hover:bg-violet-400 text-white text-xs h-9">→</Button>
                ) : null}
              </div>
            </ChatInputArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STEP: Confirm */}
      <AnimatePresence>
        {[STEP.CONFIRM, STEP.GENERATING, STEP.DONE].includes(chatStep) && (
          <motion.div key="confirm-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <UserBubble>{language.toUpperCase()} · {aspectRatio} ✓</UserBubble>
            <AssistantBubble>
              <p className="font-medium">Ready to generate!</p>
              <div className="mt-2 text-[12px] text-[#9CA3AF] space-y-0.5">
                <p>📝 {script.trim().split(/\s+/).length} words · {script.trim().split(/\n{2,}/).filter(Boolean).length} scene{script.trim().split(/\n{2,}/).filter(Boolean).length === 1 ? '' : 's'}</p>
                <p>🎭 {selectedLook?.name || 'Original look'}</p>
                <p>🎙️ {selectedVoice ? (selectedVoice.display_name || selectedVoice.name) : 'Default voice'}</p>
                <p>🌐 {LANGUAGES.find((l) => l.code === language)?.label || language} · {aspectRatio}</p>
              </div>
              {genError ? <p className="text-rose-400 text-[12px] mt-2">⚠ {genError}</p> : null}
            </AssistantBubble>
            {chatStep === STEP.CONFIRM ? (
              <ChatInputArea>
                <Button onClick={startGeneration} disabled={generating} className="bg-violet-500 hover:bg-violet-400 text-white font-semibold">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TwinneeLogo className="w-4 h-4 mr-2" />}
                  Generate avatar video
                </Button>
              </ChatInputArea>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>

      {/* STEP: Generating */}
      <AnimatePresence>
        {chatStep === STEP.GENERATING ? (
          <motion.div key="generating-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <AssistantBubble>
              <div className="space-y-1.5">
                <p className="font-medium text-violet-200">Generating your avatar video…</p>
                <Progress value={genProgress} className="h-1.5" />
                <p className="text-[11px] text-[#6B7280]">{genStatus || 'Processing…'} · {genProgress}%</p>
              </div>
            </AssistantBubble>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* STEP: Done */}
      <AnimatePresence>
        {chatStep === STEP.DONE ? (
          <motion.div key="done-step" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <AssistantBubble>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                <p className="font-medium text-green-300">Avatar video ready! Check the panel on the right.</p>
              </div>
            </AssistantBubble>
            <ChatInputArea>
              <div className="flex gap-2 flex-wrap">
                {videoUrl ? (
                  <a href={videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-semibold hover:bg-green-500/30 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Open video
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate('/avatar-cast')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white text-xs transition-colors cursor-pointer"
                >
                  <Clapperboard className="w-3.5 h-3.5" /> Open full AvatarCast studio
                </button>
              </div>
            </ChatInputArea>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div ref={chatEndRef} />
    </>
  );

  /* ── Right panel: status + video ── */
  const rightContent = (
    <div className="px-4 sm:px-6 py-5 space-y-6">
      {/* Blueprint / status */}
      {blueprint || chatStep === STEP.GENERATING || chatStep === STEP.DONE ? (
        <>
          <RightPanelHeading>Generation status</RightPanelHeading>
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 space-y-3">
            {/* step list */}
            {[
              { id: 'blueprint', label: 'Blueprint created', done: Boolean(blueprint) },
              { id: 'tts', label: 'Voice synthesis', done: genProgress >= 30 },
              { id: 'assets', label: 'Scene generation', done: genProgress >= 60 },
              { id: 'render', label: 'Final render', done: genProgress >= 90 || chatStep === STEP.DONE },
            ].map((s) => (
              <div key={s.id} className="flex items-center gap-3 text-[13px]">
                {s.done ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                ) : chatStep === STEP.GENERATING ? (
                  <Loader2 className="w-4 h-4 text-violet-400 animate-spin shrink-0" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-white/20 shrink-0" />
                )}
                <span className={s.done ? 'text-white' : 'text-[#6B7280]'}>{s.label}</span>
              </div>
            ))}
            {chatStep === STEP.GENERATING ? (
              <div className="pt-1">
                <Progress value={genProgress} className="h-1.5" />
                <p className="text-[11px] text-[#6B7280] mt-1">{genProgress}% · {genStatus || 'Processing…'}</p>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {/* Final video */}
      {videoUrl ? (
        <>
          <RightPanelHeading>Your avatar video</RightPanelHeading>
          <div className="rounded-xl overflow-hidden border border-white/[0.07] bg-black aspect-video">
            <video src={videoUrl} controls className="w-full h-full object-contain" />
          </div>
          <a
            href={videoUrl}
            download
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs font-semibold hover:bg-violet-500/30 transition-colors"
          >
            <Film className="w-3.5 h-3.5" /> Download video
          </a>
        </>
      ) : null}

      {/* Scene breakdown from blueprint */}
      {blueprint?.scenes?.length && chatStep !== STEP.DONE ? (
        <>
          <RightPanelHeading>Scene breakdown</RightPanelHeading>
          <div className="space-y-2">
            {blueprint.scenes.map((scene, idx) => (
              <div key={scene.scene_id || idx} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-[12px]">
                <p className="text-[10px] text-[#6B7280] font-mono mb-0.5">Scene {idx + 1}</p>
                <p className="text-[#E5E7EB] leading-relaxed line-clamp-2">{scene.script}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );

  /* ── header actions ── */
  const headerRight = (
    <button
      type="button"
      onClick={() => navigate('/avatar-cast')}
      className="h-8 px-3 rounded-full border border-white/10 text-[11px] text-[#9CA3AF] hover:text-white hover:bg-white/5 flex items-center gap-1.5 cursor-pointer"
    >
      <Clapperboard className="w-3.5 h-3.5" /> Full studio
    </button>
  );

  return (
    <FlowChatLayout
      flowLabel="AvatarCast"
      flowColor="#8b5cf6"
      onBack={() => navigate('/dashboard')}
      headerRight={headerRight}
      leftContent={leftContent}
      rightContent={rightContent}
      showRight={chatStep !== STEP.SCRIPT}
    />
  );
}

export default AvatarCastChatPage;
